"use client";

import React, { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { Showcase_Badge_data } from "@/lib/utils";

interface LinkdhinBadgesProps {
  className?: string;
}
const cardSizes = [
  { w: 244, h: 418 },
  { w: 196, h: 336 },
  { w: 175, h: 300 },
  { w: 150, h: 256 },
  { w: 125, h: 214 },
];
const mobileCardSizes = [
  { w: 138, h: 235 },
  { w: 110, h: 190 },
  { w: 99, h: 169 },
];

const Linkdhin_Badges: React.FC<LinkdhinBadgesProps> = ({ className = "" }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const paginationRef = React.useRef<HTMLDivElement>(null);
  const badges = Showcase_Badge_data;
  const totalSlides = badges.length;

  useEffect(() => {
    if (!paginationRef.current) return;

    const activeDot = paginationRef.current.children[selectedIndex];
    if (!activeDot) return;

    activeDot.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedIndex((prev) => (prev + 1) % totalSlides);
    }, 3000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  const scrollTo = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  const getVisibleSlides = (isMobile: boolean) => {
    const visible = [];
    const range = isMobile ? 2 : 4;
    for (let i = -range; i <= range; i++) {
      let idx = selectedIndex + i;
      if (idx < 0) idx += totalSlides;
      if (idx >= totalSlides) idx -= totalSlides;
      visible.push({ index: idx, position: i });
    }
    return visible;
  };

  const desktopSlides = getVisibleSlides(false);
  const mobileSlides = getVisibleSlides(true);

  return (
    <section className={`w-full bg-[#000] py-8 overflow-hidden ${className}`}>
      <div className="text-center mb-4">
        <h2 className="text-[20px] lg:text-[32px] font-semibold tracking-[-1.76px] text-[#FFBA08] leading-[1.1] md:leading-[1.1]">
          Showcase Badge on LinkedIn
        </h2>
        <p className="text-[#FFF]  text-sm md:text-base font-semibold">
          Join 1,000s of AI-Ready Accountants
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-4">
        <div className="relative h-[450px] hidden md:flex items-center justify-center">
          {desktopSlides?.map(({ index, position }) => {
            const absPosition = Math.abs(position);
            const sizeIndex = Math.min(absPosition, cardSizes.length - 1);
            const size = cardSizes[sizeIndex];
            const desktopOffset = position * 80;
            const zIndex = 10 - absPosition;
            const opacity =
              absPosition === 0
                ? 1
                : absPosition === 1
                  ? 0.9
                  : absPosition === 2
                    ? 0.7
                    : absPosition === 3
                      ? 0.5
                      : 0.3;

            return (
              <div
                key={index}
                className="absolute transition-all duration-500 ease-out cursor-pointer rounded-xl overflow-hidden shadow-xl bg-white"
                style={{
                  zIndex,
                  opacity,
                  width: size.w,
                  height: size.h,
                  transform: `translateX(${desktopOffset}px)`,
                }}
                onClick={() => scrollTo(index)}
              >
                <Image
                  src={badges[index]}
                  alt={`Badge ${index + 1}`}
                  width={size.w}
                  height={size.h}
                  className="w-full h-full object-contain"
                />
              </div>
            );
          })}
        </div>
        <div className="relative h-[260px] md:hidden flex items-center justify-center">
          {mobileSlides?.map(({ index, position }) => {
            const absPosition = Math.abs(position);
            const sizeIndex = Math.min(absPosition, mobileCardSizes.length - 1);
            const mobileSize = mobileCardSizes[sizeIndex];
            const mobileOffset = position * 45;
            const zIndex = 10 - absPosition;
            const opacity =
              absPosition === 0 ? 1 : absPosition === 1 ? 0.85 : 0.6;

            return (
              <div
                key={index}
                className="absolute transition-all duration-500 ease-out cursor-pointer rounded-lg overflow-hidden shadow-lg bg-white"
                style={{
                  zIndex,
                  opacity,
                  width: mobileSize.w,
                  height: mobileSize.h,
                  transform: `translateX(${mobileOffset}px)`,
                }}
                onClick={() => scrollTo(index)}
              >
                <Image
                  src={badges[index]}
                  alt={`Badge ${index + 1}`}
                  width={mobileSize.w}
                  height={mobileSize.h}
                  className="w-full h-full object-contain"
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Linkdhin_Badges;
